import React, { useState } from 'react';
import { Alert, Button, Card, CardBody, CustomInput, InputGroup, InputGroupAddon } from 'reactstrap';
import { toast } from 'react-toastify';
import LoadingOverlay from 'react-loading-overlay-ts';
import { parseCSVToRecipientRecords } from './csvparser';
import { IndexMapping, RecipientLeadSheetRecord } from '../../types/organizer';
import RecipientPreviewTable from './studentpreviewtable';
import IndexMappingInterface from './indexmappinginterface';
import { getBulkImportUploadUrl, uploadBulkImportCSV, processBulkImport } from '../../api/organizer';
import { useMetaProvider } from '../../contexts/MetaContext';
import styles from '../../styles/archiveui.module.css';

interface BulkImportProps {
  accountId: string;
}

const BulkImport = ({ accountId }: BulkImportProps): JSX.Element => {
  const { metaState } = useMetaProvider();
  const [selectedFile, setSelectedFile] = useState<File>();
  const [records, setRecords] = useState<RecipientLeadSheetRecord[]>([]);
  const [mappableColumns, setMappableColumns] = useState<string[]>([]);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [fileInputKey, setFileInputKey] = useState<number>(0);
  const [indexMapping, setIndexMapping] = useState<IndexMapping>();
  const [showMapping, setShowMapping] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const fileChange = (e: React.FormEvent<HTMLInputElement>) => {
    setError(undefined);
    if (e.currentTarget.files && e.currentTarget.files.length > 0) {
      const f = e.currentTarget.files.item(0);
      if (f) setSelectedFile(f);
    }
  };

  const parseFile = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }
    setLoading(true);
    setError(undefined);
    try {
      const fileText = await selectedFile.text();
      const result = parseCSVToRecipientRecords(fileText);
      setRecords(result.records);
      setMappableColumns(result.mappableColumns);
      setShowMapping(true);
      toast.success(`CSV parsed successfully. ${result.totalRows} records found. Please map the columns.`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to parse CSV file';
      setError(msg);
      toast.error(`Failed to parse CSV: ${msg}`);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingComplete = (mapping: IndexMapping) => {
    setIndexMapping(mapping);
    setShowMapping(false);
    toast.success('Column mapping saved successfully. You can now submit for processing.');
  };

  const handleMappingCancel = () => {
    setShowMapping(false);
    setRecords([]);
    setMappableColumns([]);
    setSelectedFile(undefined);
    setFileInputKey((prev) => prev + 1);
  };

  const submitForProcessing = async () => {
    if (!selectedFile) {
      setError('No file selected');
      return;
    }
    if (!indexMapping) {
      setError('Column mapping not complete. Please map CSV columns to document indexes.');
      return;
    }
    if (records.length === 0) {
      setError('No records to process');
      return;
    }

    setIsProcessing(true);
    setError(undefined);
    setUploadProgress(0);
    try {
      toast.info('Preparing upload...');
      const { uploadUrl, fields, s3Key } = await getBulkImportUploadUrl(accountId);
      toast.info('Uploading CSV file...');
      await uploadBulkImportCSV(selectedFile, uploadUrl, fields, (progress) => setUploadProgress(progress));
      toast.info('Starting processing...');
      const response = await processBulkImport(accountId, s3Key, indexMapping);
      if (response.success) {
        toast.success(response.message);
        clearAll();
      } else {
        setError('Failed to start processing');
        toast.error('Failed to start processing');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to process bulk import';
      setError(msg);
      toast.error(`Processing failed: ${msg}`);
    } finally {
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  const clearAll = () => {
    setSelectedFile(undefined);
    setRecords([]);
    setMappableColumns([]);
    setError(undefined);
    setIndexMapping(undefined);
    setShowMapping(false);
    setFileInputKey((prev) => prev + 1);
  };

  return (
    <div>
      <LoadingOverlay
        active={loading || isProcessing}
        spinner
        text={
          isProcessing
            ? uploadProgress > 0 && uploadProgress < 100
              ? `Uploading... ${Math.round(uploadProgress)}%`
              : 'Processing your file...'
            : 'Parsing CSV...'
        }
      >
        <Card style={{ marginTop: '1em', borderRadius: '.5em' }}>
          <CardBody className={styles.searchBarHeader}>
            <h5 style={{ textAlign: 'start', fontWeight: 500, marginTop: '.5em' }}>Bulk Import Lead Sheets</h5>
          </CardBody>
          <CardBody className={styles.searchBarBody}>
            <div className="row">
              <div className="col">
                <InputGroup>
                  <CustomInput key={fileInputKey} id="csvFileInput" type="file" onChange={fileChange} accept=".csv" />
                  <InputGroupAddon addonType="append">
                    <Button className={styles.scrbBtnBlue} disabled={!selectedFile} onClick={parseFile}>
                      Parse File
                    </Button>
                    <Button color="danger" onClick={clearAll} disabled={!selectedFile && records.length === 0}>
                      Clear
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
              </div>
            </div>
            {error && (
              <Alert color="danger" style={{ marginTop: '1em', marginBottom: '0px' }}>
                {error}
              </Alert>
            )}
            <div style={{ marginTop: '1em', fontSize: '0.9em', color: '#666' }}>
              <strong>Required CSV format:</strong>
              <div style={{ marginTop: '0.5em', fontFamily: 'monospace', fontSize: '0.85em' }}>UniqueID,FirstName,LastName,DateOfBirth,DocumentType</div>
              <div style={{ marginTop: '0.5em' }}>
                Only <strong>DocumentType</strong> header is required. Add any extra columns as needed — they will appear in the mapping step.
              </div>
            </div>
          </CardBody>
        </Card>

        {showMapping && metaState && (
          <IndexMappingInterface
            metadata={metaState}
            mappableColumns={mappableColumns}
            onMappingComplete={handleMappingComplete}
            onCancel={handleMappingCancel}
          />
        )}

        {records.length > 0 && !showMapping && indexMapping && (
          <Card style={{ marginTop: '1em', borderRadius: '.5em' }}>
            <CardBody>
              <Alert color="info" style={{ marginBottom: '1em' }}>
                <strong>Step 3 of 3:</strong> Review your data and submit for processing. You will receive an email with the results.
              </Alert>
              {records.length > 5000 && (
                <Alert color="warning" style={{ marginBottom: '1em' }}>
                  <strong>Large File:</strong> Your file contains {records.length.toLocaleString()} records. Processing may take several minutes.
                </Alert>
              )}
              <div style={{ marginBottom: '1em', fontSize: '0.9em', color: '#666' }}>
                <strong>Preview:</strong> Showing first {Math.min(100, records.length)} of {records.length} records
              </div>
              <RecipientPreviewTable records={records.slice(0, 100)} />
              <Alert color="info" style={{ marginTop: '1em' }}>
                <strong>What happens next:</strong>
                <ul style={{ marginTop: '0.5em', marginBottom: 0 }}>
                  <li>Your file will be validated against document type requirements</li>
                  <li>If validation passes, a PDF with lead sheets will be generated and emailed to you (download link valid 7 days)</li>
                  <li>If there are validation errors, you&apos;ll receive an email with a CSV listing the row numbers and error messages</li>
                </ul>
              </Alert>
              <div style={{ marginTop: '1em', display: 'flex', gap: '0.5em', justifyContent: 'flex-end' }}>
                <Button className={styles.scrbBtnBlue} onClick={submitForProcessing} disabled={isProcessing}>
                  {isProcessing ? 'Processing...' : 'Submit for Processing'}
                </Button>
              </div>
            </CardBody>
          </Card>
        )}
      </LoadingOverlay>
    </div>
  );
};

export default BulkImport;
